/**
 * Parent Data tab — per-student factual snapshot for the current week.
 * Alphabetical by first name. No narrative. Subject-scoped.
 *
 * Per spec §8 — fields:
 *   Student name + class
 *   Accuracy + trend
 *   Consistency (N/7 days)
 *   Questions this week
 *   Open thinking gaps (count)
 *   Last active
 *
 * Empty state per plan §4.7:
 *   "Parent data appears once students start practicing." (when zero
 *   students have any activity)
 */

import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '../../services/api';

const SUBJECT_LABEL = { physics: 'Physics', chemistry: 'Chemistry', mathematics: 'Maths' };

function trendArrow(direction) {
  if (direction === 'up') return <span className="text-teacher-green">↑</span>;
  if (direction === 'down') return <span className="text-teacher-red">↓</span>;
  return <span className="text-teacher-text-tertiary">→</span>;
}

function trendDeltaText(deltaPct) {
  const sign = deltaPct > 0 ? '+' : '';
  return deltaPct === 0 ? '0%' : `${sign}${deltaPct}%`;
}

function lastActiveColor(label) {
  if (label === 'today') return 'text-teacher-green';
  if (label === 'yesterday') return 'text-teacher-amber';
  if (typeof label === 'string' && label.includes('day')) {
    const days = parseInt(label, 10);
    if (days >= 5) return 'text-teacher-red';
    if (days >= 3) return 'text-teacher-amber';
  }
  return 'text-teacher-text-tertiary';
}

const SUBJECT_AVATAR_PALETTE = [
  ['bg-teacher-purple-light', 'text-teacher-purple'],
  ['bg-teacher-blue-light',   'text-teacher-blue'],
  ['bg-teacher-green-light',  'text-teacher-green'],
  ['bg-teacher-amber-light',  'text-teacher-amber'],
  ['bg-pink-100',             'text-teacher-pink'],
];
function avatarStyleFor(studentId) {
  let h = 0;
  for (let i = 0; i < (studentId || '').length; i++) h = ((h << 5) - h + studentId.charCodeAt(i)) | 0;
  return SUBJECT_AVATAR_PALETTE[Math.abs(h) % SUBJECT_AVATAR_PALETTE.length];
}
function initials(name) {
  return (name || 'S').split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('') || 'S';
}

function StudentRow({ student }) {
  const [bg, fg] = avatarStyleFor(student.student_id);
  const trend = student.accuracy_trend || { direction: 'flat', delta_pct: 0 };

  return (
    <div className="border border-teacher-border rounded-xl overflow-hidden mb-2.5">
      <div className="px-3.5 py-2.5 bg-teacher-bg border-b border-teacher-border flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-medium shrink-0 ${bg} ${fg}`}>
            {initials(student.name)}
          </div>
          <div className="min-w-0">
            <div className="text-[13px] font-medium truncate">{student.name}</div>
            <div className="text-[11px] text-teacher-text-secondary">
              {student.class ? `Class ${student.class}` : ''}
            </div>
          </div>
        </div>
        <div className="flex gap-4 text-xs whitespace-nowrap">
          <div>
            <span className="text-teacher-text-secondary">Accuracy </span>
            <strong>
              {student.accuracy_pct || 0}%{' '}
              {trendArrow(trend.direction)}
              <span className="font-normal ml-0.5">{trendDeltaText(trend.delta_pct || 0)}</span>
            </strong>
          </div>
          <div>
            <span className="text-teacher-text-secondary">Active </span>
            <strong>{student.consistency_days_out_of_7 || 0}/7 days</strong>
          </div>
          <div>
            <span className="text-teacher-text-secondary">Questions </span>
            <strong>{student.questions_last_7d || 0}</strong>
          </div>
        </div>
      </div>
      <div className="px-3.5 py-2.5 flex items-center gap-5 text-[11px]">
        <div>
          <span className="text-teacher-text-secondary">Open gaps: </span>
          <span className={`font-medium ${student.open_gap_count > 0 ? 'text-teacher-red' : 'text-teacher-green'}`}>
            {student.open_gap_count || 0}
          </span>
        </div>
        <div className={`ml-auto ${lastActiveColor(student.last_active)}`}>
          Last active: {student.last_active}
        </div>
      </div>
    </div>
  );
}

export default function ContextParentDataTab() {
  const { batchId, subject } = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.getTeacherParentDataV2(batchId, subject)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [batchId, subject]);

  if (loading) {
    return <div className="p-6 text-sm text-teacher-text-secondary">Loading…</div>;
  }
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-teacher-red-light border border-teacher-red text-teacher-red rounded-lg p-3 text-sm">
          Error: {error}
        </div>
      </div>
    );
  }

  const subjectLabel = SUBJECT_LABEL[subject] || subject;
  const students = data?.students || [];
  const anyActivity = students.some(s => (s.questions_last_7d || 0) > 0);

  return (
    <div className="p-6">
      <h1 className="text-base font-semibold text-teacher-text-primary mb-4">
        Student data snapshot · {subjectLabel}
      </h1>

      {students.length === 0 ? (
        <div className="bg-white border border-teacher-border rounded-xl p-8 text-center text-sm text-teacher-text-secondary">
          No students in this batch yet.
        </div>
      ) : !anyActivity ? (
        <div className="bg-white border border-teacher-border rounded-xl p-8 text-center text-sm text-teacher-text-secondary">
          Parent data appears once students start practicing.
        </div>
      ) : (
        <div>
          {students.map(s => <StudentRow key={s.student_id} student={s} />)}
        </div>
      )}
    </div>
  );
}
